// In-memory cache of the full messages opened (or prefetched) this session, so
// going back to an email shows its body at once instead of a spinner while
// GET /api/mail/messages/:id runs again.
//
// The IndexedDB cache (mailCache.js) keeps rows and stripped text only — never
// bodyHtml or attachments — so before this every re-open waited on the network,
// and on Graph's bad minutes that was seconds for an email read a moment ago.
//
// What a hit shows, and why it is safe:
// - The body, attachments and addresses never change for a sent or received
//   email, so the cached copy is the email. Drafts DO change, so the caller
//   never caches or paints anything in Drafts (and nothing degraded: a body
//   whose attachments listing timed out is not kept).
// - Read / flag / done state DOES change (here, in Outlook, on the phone), so
//   it comes from the list row the person just clicked — the freshest state the
//   app holds — with the in-flight op guards on top, exactly as hydrateBody
//   applies them. The cached copy's own state is never shown.
// - The normal GET still runs after the paint and replaces it, as before. When
//   the HTML is the same string the reading pane's iframe is not reloaded.
//
// One cache per signed-in mailbox: App holds it in a ref inside AuthedApp,
// which is keyed by mailbox, so switching mailbox or signing out drops it.

export const BODY_CACHE_MAX = 40;               // emails
export const BODY_CACHE_MAX_CHARS = 8_000_000;  // bodyHtml + bodyText, ~16 MB at most

// Arrow-key skimming: an ArrowDown/Up this soon after the previous one fetches
// the email after the next one ahead (see MessageList). Short enough that the
// prefetch is nearly always used inside App's 10 s prefetch window — a person
// reading each email at length never triggers one, so no Graph calls are added
// for them.
export const ARROW_SKIM_MS = 3000;

// The state the list row owns. Everything else comes from the cached body.
const ROW_STATE = ['isRead', 'flagged', 'completed', 'replied', 'forwarded'];

export function isCacheableBody(full) {
  return !!full && typeof full === 'object'
    && Array.isArray(full.attachments)
    && !full.attachmentsUnavailable
    && !full._bodyLoadError;
}

const sizeOf = full => (typeof full.bodyHtml === 'string' ? full.bodyHtml.length : 0)
  + (typeof full.bodyText === 'string' ? full.bodyText.length : 0);

export function createBodyCache({ max = BODY_CACHE_MAX, maxChars = BODY_CACHE_MAX_CHARS } = {}) {
  const map = new Map();   // id → { full, size }, oldest first
  let chars = 0;
  const drop = (id) => {
    const e = map.get(id);
    if (e) { map.delete(id); chars -= e.size; }
  };
  return {
    get(id) {
      const e = map.get(id);
      if (!e) return null;
      map.delete(id); map.set(id, e);   // most recently used
      return e.full;
    },
    has: id => map.has(id),
    // Keep a full message. Anything not cacheable — or one email so large it
    // would push a quarter of the cache out — is not kept, and an older copy
    // under the same id is dropped so a re-open never shows it.
    set(id, full) {
      drop(id);
      if (!id || !isCacheableBody(full)) return false;
      const size = sizeOf(full);
      if (size > maxChars / 4) return false;
      map.set(id, { full, size });
      chars += size;
      while (map.size > max || chars > maxChars) drop(map.keys().next().value);
      return true;
    },
    delete: drop,
    clear() { map.clear(); chars = 0; },
    get size() { return map.size; },
    get chars() { return chars; },
  };
}

// What the reading pane shows on a re-open: the cached body, the row's current
// state, the op guards (recentFlagOp / recentReadOp entries) last, and the
// row's label (labels live in Postgres; Graph's message never carries one).
export function withCachedBody(row, full, { flagOp = null, readOp = null } = {}) {
  const out = { ...full };
  for (const f of ROW_STATE) if (row[f] !== undefined) out[f] = row[f];
  if (flagOp) { out.flagged = flagOp.flagged; out.completed = flagOp.completed; }
  if (readOp) out.isRead = readOp.isRead;
  out.label = row.label || full.label;
  return out;
}
